import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject, Directive, Optional, Input, HostListener, NgModule } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, switchMap, mapTo } from 'rxjs';
import { takeUntil, debounceTime, filter, first, delay } from 'rxjs/operators';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { NgbDate, NgbDateNativeUTCAdapter } from '@ng-bootstrap/ng-bootstrap';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

/* eslint-disable @typescript-eslint/ban-types */
class AnalyticsService {
    constructor(_document, platformId, _ngZone, router) {
        this._document = _document;
        this._ngZone = _ngZone;
        this.router = router;
        this.checkExisting = true;
        this.loaded$ = new BehaviorSubject(false);
        this._isInitialized = false;
        this._application = '';
        this._isBrowser = false;
        this._httpInterceptorUrlFilter = (url) => url.includes('.geneplanet.com/api/');
        this._isBrowser = isPlatformBrowser(platformId);
        if (this._isBrowser) {
            window.addEventListener('analytics-loaded', () => {
                this.loaded$.next(true);
            });
        }
    }
    // Check if init() was already called
    get isInitialized() {
        return this._isInitialized;
    }
    // Current user
    get user() {
        return this._user;
    }
    // Apply custom URL validator for Analytics Interceptor
    set httpInterceptorUrlFilter(value) {
        this._httpInterceptorUrlFilter = value;
    }
    // Set user and call identify and alias if userId was changed
    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    set user(value) {
        if (!this._isInitialized) {
            return;
        }
        if (this._user && this._user.userId !== value.userId) {
            this.alias(value.userId, this._user.userId);
        }
        this.identify(value);
        this._user = value;
    }
    set locale(value) {
        if (!this._isInitialized) {
            return;
        }
        this._locale = value;
    }
    // Initialize service
    init(application, writeKey, dataPlaneUrl) {
        if (this._isInitialized || !this._isBrowser) {
            return;
        }
        this._isInitialized = true;
        this._application = application;
        this.injectRudderScript(writeKey, dataPlaneUrl);
        this.trackPageChanges();
        this.trackClicks();
    }
    loaded() {
        return this.loaded$.asObservable();
    }
    setUser(value) {
        if (!this._isInitialized) {
            return;
        }
        if (!window.rudderanalytics.getUserId() && this.checkExisting) {
            this.user = value;
        }
    }
    getAnonymousId() {
        return ((this._isInitialized && window.rudderanalytics.getAnonymousId()) || '');
    }
    getUserId() {
        return (this._isInitialized && window.rudderanalytics.getUserId()) || '';
    }
    // Check if anonymousId and userId should be passed to the HttpRequest (used by analyticsInterceptor)
    isTrackableHttpRequest(url) {
        return this._httpInterceptorUrlFilter(url);
    }
    // Track controls value changes, also handles nested FormGroups.
    trackForm(form, controls, unsubscribe) {
        if (!this._isInitialized) {
            return;
        }
        const destroyOperator = unsubscribe instanceof Observable ? takeUntil(unsubscribe) : unsubscribe;
        for (const key of Object.keys(controls)) {
            const control = controls[key];
            if (control instanceof UntypedFormGroup) {
                this.trackForm(form, control.controls, unsubscribe);
            }
            else {
                control.valueChanges
                    .pipe(destroyOperator, debounceTime(500))
                    .subscribe((val) => {
                    const value = {
                        value: val instanceof NgbDate
                            ? new NgbDateNativeUTCAdapter().toModel(val).toISOString()
                            : val,
                        isValid: String(control.valid),
                    };
                    this.track(AnalyticsEvents.INPUT_VALUE_CHANGE, {
                        form,
                        input: key,
                        value,
                    });
                });
            }
        }
    }
    trackFormValue(form, input, value, formId, inputId) {
        if (!this._isInitialized) {
            return;
        }
        this.track(AnalyticsEvents.INPUT_VALUE_CHANGE, {
            form,
            formId,
            input,
            inputId,
            value,
        });
    }
    reset() {
        if (!this._isInitialized) {
            return;
        }
        this._user = { userId: '', traits: {} };
        this._ngZone.runOutsideAngular(() => {
            window.rudderanalytics.reset(false);
        });
    }
    setAnonymousId(anonymousId) {
        if (!this._isInitialized) {
            return;
        }
        this._ngZone.runOutsideAngular(() => {
            window.rudderanalytics.setAnonymousId(anonymousId);
        });
    }
    trackClicksManually(data) {
        if (!this._isInitialized) {
            return;
        }
        this.track(AnalyticsEvents.CLICK, { data });
    }
    trackHover(data) {
        if (!this._isInitialized) {
            return;
        }
        this.track(AnalyticsEvents.HOVER, { data });
    }
    trackClaim(barcode, kitType, collector) {
        if (!this._isInitialized) {
            return;
        }
        this.track(AnalyticsEvents.CLAIM, {
            barcode,
            kitType,
            collector,
        });
    }
    track(event, properties) {
        if (!this._isInitialized) {
            return;
        }
        this._ngZone.runOutsideAngular(() => {
            window.rudderanalytics.track(event, {
                ...properties,
                application: this._application,
            }, { locale: this._locale });
        });
    }
    pageTrack(url) {
        this.page(this._application, url, {});
    }
    // Append Rudder script into HTML body
    injectRudderScript(writeKey, dataPlaneUrl) {
        const script = document.createElement('script');
        script.type = `application/javascript`;
        script.text = `
      !function(){var e=window.rudderanalytics=window.rudderanalytics||[];e.methods=["load","page","track","identify","alias","group","ready","reset","getAnonymousId","setAnonymousId","getUserId"],e.factory=function(t){return function(){var r=Array.prototype.slice.call(arguments);return r.unshift(t),e.push(r),e}};for(var t=0;t<e.methods.length;t++){var r=e.methods[t];e[r]=e.factory(r)}e.loadJS=function(e,t){var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.rudderlabs.com/v1.1/rudder-analytics.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a)},e.loadJS(),
      e.load("${writeKey}","${dataPlaneUrl}"); e.ready(()=>window.dispatchEvent(new Event("analytics-loaded")))}();
    `;
        this._document.body.appendChild(script);
    }
    // Track element clicks if "data-track" attribute is provided. Also tracking if ancestor has the attribute.
    trackClicks() {
        this._ngZone.runOutsideAngular(() => {
            document.onclick = (event) => {
                const target = event.target;
                let data = target?.getAttribute('data-track');
                // If data-track is not present, check ancestors for attribute.
                if (!data) {
                    const newTarget = target.closest('[data-track]');
                    data = newTarget?.getAttribute('data-track');
                }
                if (data) {
                    this._ngZone.run(() => this.track(AnalyticsEvents.CLICK, {
                        data: data,
                        class: target?.className,
                    }));
                }
            };
        });
    }
    trackPageChanges() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd), switchMap((event) => this._ngZone.onMicrotaskEmpty.pipe(first(), mapTo(event))), delay(0))
            .subscribe((event) => this.page(this._application, event.url, {}));
    }
    identify(user) {
        this._ngZone.runOutsideAngular(() => {
            window.rudderanalytics.identify(user.userId, {
                ...user.traits,
                application: this._application,
            }, { locale: this._locale });
        });
    }
    alias(to, from) {
        this._ngZone.runOutsideAngular(() => {
            window.rudderanalytics.alias(to, from, {
                application: this._application,
                locale: this._locale,
            });
        });
    }
    page(category, name, properties) {
        this._ngZone.runOutsideAngular(() => {
            window.rudderanalytics.page(category, name, {
                ...properties,
                application: this._application,
            }, { locale: this._locale });
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsService, deps: [{ token: DOCUMENT }, { token: PLATFORM_ID }, { token: i0.NgZone }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.NgZone }, { type: i1.Router }]; } });
var AnalyticsEvents;
(function (AnalyticsEvents) {
    AnalyticsEvents["CLICK"] = "click";
    AnalyticsEvents["INPUT_VALUE_CHANGE"] = "input_value_change";
    AnalyticsEvents["HOVER"] = "hover";
    AnalyticsEvents["CLAIM"] = "claim";
})(AnalyticsEvents || (AnalyticsEvents = {}));

class AnalyticsInterceptor {
    constructor(analyticsService) {
        this.analyticsService = analyticsService;
    }
    intercept(request, next) {
        // Check if http request should be tracked, and provide anonymous ID and userID to backend, for tracking.
        if (this.analyticsService.isTrackableHttpRequest(request.url)) {
            request = request.clone({
                headers: request.headers
                    .set('rs-anonymous-id', this.analyticsService.getAnonymousId())
                    .set('rs-user-id', this.analyticsService.getUserId())
            });
        }
        return next.handle(request);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsInterceptor, deps: [{ token: AnalyticsService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AnalyticsService }]; } });

const ANALYTICS_TOKEN = 'UtilsAnalyticsService';

class GptDataTrackDirective {
    constructor(elementRef, renderer, analyticsService) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.analyticsService = analyticsService;
    }
    ngOnInit() {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'gptDataTrack', this.gptDataTrack ?? '');
    }
    onClick() {
        if (this.gptDataTrack) {
            this.analyticsService?.trackClicksManually(this.gptDataTrack);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: GptDataTrackDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: ANALYTICS_TOKEN, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.3", type: GptDataTrackDirective, selector: "[gptDataTrack]", inputs: { gptDataTrack: "gptDataTrack" }, host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: GptDataTrackDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[gptDataTrack]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: AnalyticsService, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ANALYTICS_TOKEN]
                }] }]; }, propDecorators: { gptDataTrack: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

const directives = [GptDataTrackDirective];
class AnalyticsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsModule, declarations: [GptDataTrackDirective], imports: [CommonModule], exports: [GptDataTrackDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsModule, providers: [
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AnalyticsInterceptor,
                multi: true
            }
        ], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.3", ngImport: i0, type: AnalyticsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...directives],
                    imports: [CommonModule],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: AnalyticsInterceptor,
                            multi: true
                        }
                    ],
                    exports: [...directives],
                }]
        }] });

var AnalyticsIdentifyType;
(function (AnalyticsIdentifyType) {
    AnalyticsIdentifyType["HI_NORMAL"] = "hi-normal";
    AnalyticsIdentifyType["DNA"] = "claim-dna";
    AnalyticsIdentifyType["BLOOD"] = "claim-blood";
    AnalyticsIdentifyType["NIPT"] = "nipt2dash";
    AnalyticsIdentifyType["ADRIALAB"] = "lab-adrialab";
    AnalyticsIdentifyType["BIOLAB"] = "lab-biolab";
})(AnalyticsIdentifyType || (AnalyticsIdentifyType = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsIdentifyType, AnalyticsInterceptor, AnalyticsModule, AnalyticsService, GptDataTrackDirective };
